/* eslint-disable import/extensions,import/no-unresolved,import/no-unresolved,import/no-duplicates,import/no-named-as-default-member,no-constant-condition,no-console */
/**
 * Gets the repositories of the user from Github
 */
import _, { take } from 'lodash';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import moment from 'moment';
import request from 'utils/request';
import apiConfig from 'utils/apiConfig';
import { combineDateTime } from 'utils/dateUtil';
import { startProgress, endProgress } from 'containers/ProgressBar/actions';
import { setClaimTypes, setChildBenefits, getInitClaimTypeSuccess } from 'containers/App/actions';
import { mapCountryList, mapCityList, mapNewFormatCountryList } from '../dataMapper/countryMapper';
import {
  GET_CITY_LIST,
  GET_PROVINCE_LIST,
  GET_COUNTRY_LIST,
  GET_COUNTRY_SUPPORTED_LIST,
  GET_CLAIM_TYPES,
  VALIDATE_DATE_TIME_OF_EVENT,
  TRIGGER_WHAT_HAPPENED_VALIDATION,
} from './constants';
import {
  getCityListSuccess,
  getProvinceListSuccess,
  getCountryListSuccess,
  getCountriesOfxSupportedSuccess,
  handleFormDataChange,
  getClaimTypeSuccess,
  policyVersionChanged,
  triggerWhatHappenedValidationSuccess,
  triggerWhatHappenedValidationFailure,
} from './actions';
import { makeSelectAuthToken } from '../../Login/selectors';
import { makeSelectStepWhatHappened, makeSelectOldEventDate } from './selectors';
import { makeError } from '../../Wizard/actions';
import { makeSelectPolicyId, makeSelectJSONVersion, makeSelectShowZurichUHC, makeSelectPolicyTypeCode, makeSelectPolicyPlanIds, makeSelectEnableFormDynamicPaymentDetail } from '../../App/selectors';
import { makeSelectPolicyTypeId, makeSelectClaimId } from '../../ClaimList/selectors';
import {
  logoutSuccess,
  setErrorMessageDateValidation,
} from '../../App/actions';
import isAuthorised from '../../../utils/unauthoriseValidator';
import { resetLoginInfo } from '../../Login/actions';
import { showErrorToastr } from '../../Toastr/actions';

const DATE_VALIDATION_ERROR_ID_MAP = {
  'The Date and Time of Event cannot be set in the future.': 'fastCover.stepWhatHappened.futureDateTimeWarning',
  'Date of Event selected is not within the Policy cover period. Proceed anyway?': 'fastCover.stepWhatHappened.dateOutsidePolicyWarning',
};

const CITY_DEFAULT_OPTIONS_LIMIT = 50;

function mapDateValidationErrorMessage(errorMessage) {
  return DATE_VALIDATION_ERROR_ID_MAP[errorMessage] || errorMessage;
}

/**
 * Extracts country ID from Map, object, or simple value
 * @param {*} country - Country data that can be Map, object, or ID value
 * @returns {*} - Country ID or null if not found
 */
function extractCountryId(country) {
  if (!country) return null;

  // Handle Immutable.js Map
  if (typeof country.toJS === 'function') {
    const countryObj = country.toJS();
    return countryObj.id || null;
  }

  // Handle object with id property
  if (typeof country === 'object' && country.id !== undefined) {
    return country.id;
  }

  // Handle simple value (ID itself)
  return country;
}

export function* getCountries() {
  // yield put(startProgress());
  // TODO call API to get countries
  try {
    const token = yield select(makeSelectAuthToken());
    const authToken = `bearer ${token}`;
    const requestUrl = `${apiConfig.restServiceDomain}/api/v1/Countries`;
    const jsonVersion = yield select(makeSelectJSONVersion());
    const newVersion = jsonVersion > 9;
    const requestOption = {
      method: 'GET',
      headers: {
        Authorization: authToken,
        'Content-Type': 'application/json',
      },
    };
    const response = yield call(request, requestUrl, requestOption);
    if (response.isSuccess) {
      const rs = newVersion ? mapNewFormatCountryList(response.payload) : mapCountryList(response.payload);
      yield put(getCountryListSuccess(rs));

      // Reset country and city fields if the current country is no longer valid
      const stepWhatHappenedData = yield select(makeSelectStepWhatHappened());
      const countryId = extractCountryId(stepWhatHappenedData.country);
      const isCountryValid = _.find(rs, (d) => d.id === countryId);
      
      if (countryId && !isCountryValid) {
        yield put(handleFormDataChange('form.country', ''));
        yield put(handleFormDataChange('form.city', ''));
        yield put(makeError(3));
      }
    }
  } catch (e) {
    console.log(e);
  } finally {
    // yield put(endProgress());
  }
}

export function* getCountriesOfxSupported() {
  // yield put(startProgress());
  // TODO call API to get countries
  try {
    const token = yield select(makeSelectAuthToken());
    const authToken = `bearer ${token}`;
    const enableFormDynamicPaymentDetail = yield select(makeSelectEnableFormDynamicPaymentDetail());
    const requestUrl = `${apiConfig.restServiceDomain}/api/v1/countriesOfxSupported?useDynamicFormList=${enableFormDynamicPaymentDetail}`;
    const jsonVersion = yield select(makeSelectJSONVersion());
    const newVersion = jsonVersion > 9;
    const requestOption = {
      method: 'GET',
      headers: {
        Authorization: authToken,
        'Content-Type': 'application/json',
      },
    };
    const response = yield call(request, requestUrl, requestOption);
    if (response.isSuccess) {
      const rs = newVersion ? mapNewFormatCountryList(response.payload) : mapCountryList(response.payload);
      // set data for country list
      yield put(getCountriesOfxSupportedSuccess(rs));
    }
  } catch (e) {
    console.log(e);
  } finally {
    // yield put(endProgress());
  }
}

export function* getCities({ stringCity, callback }) {
  // yield put(startProgress());
  // TODO call API to get cities
  try {
    const citySearch = (stringCity || '').trim();
    const cityOptionsLimit = CITY_DEFAULT_OPTIONS_LIMIT;

    const stepWhatHappenedData = yield select(makeSelectStepWhatHappened());
    const selectedCountry = stepWhatHappenedData.selectedCountry;
    const province = stepWhatHappenedData.province;
    const countryId = extractCountryId(selectedCountry);
    if (!countryId) {
      yield put(getCityListSuccess([]));
      if (callback) {
        callback([]);
      }
      return;
    }
    const token = yield select(makeSelectAuthToken());
    const authToken = `bearer ${token}`;

    // Build URL with province parameter if available
    let requestUrl = `${apiConfig.restServiceDomain}/api/v1/Countries/Cities?countryID=${countryId}&cityName=${encodeURIComponent(citySearch)}&maxResults=${cityOptionsLimit}`;
    if (province) {
      requestUrl += `&provinceID=${province}`;
    }

    const requestOption = {
      method: 'GET',
      headers: {
        Authorization: authToken,
        'Content-Type': 'application/json',
      },
    };
    const response = yield call(request, requestUrl, requestOption);
    if (response.isSuccess) {
      const rs = take(mapCityList(response.payload), cityOptionsLimit);
      yield put(getCityListSuccess(rs));
      if (callback) {
        callback(rs);
      }
    }
  } catch (e) {
    console.log(e);
  } finally {
    // yield put(endProgress());
  }
}

export function* getProvinces() {
  // TODO call API to get provinces
  try {
    const stepWhatHappenedData = yield select(makeSelectStepWhatHappened());
    const selectedCountry = stepWhatHappenedData.selectedCountry;
    let country = !!selectedCountry && typeof selectedCountry.toJS === 'function' ? selectedCountry.toJS() : selectedCountry;
    if (!country || typeof country !== 'object' || !country.id) {
      // If no country selected, return empty list
      yield put(getProvinceListSuccess([]));
      return;
    }
    const token = yield select(makeSelectAuthToken());
    const authToken = `bearer ${token}`;
    const requestUrl = `${apiConfig.restServiceDomain}/api/v1/Countries/Provinces?countryID=${country.id}`;
    const requestOption = {
      method: 'GET',
      headers: {
        Authorization: authToken,
        'Content-Type': 'application/json',
      },
    };
    const response = yield call(request, requestUrl, requestOption);
    if (response.isSuccess) {
      const provinces = response.payload.map((p) => ({
        value: p.id,
        label: p.name,
        code: p.code,
      }));
      yield put(getProvinceListSuccess(provinces));
    }
  } catch (e) {
    console.log(e);
  }
}

function* loadClaimTypeList(byClaimId) {
  const token = yield select(makeSelectAuthToken());
  const policyId = yield select(makeSelectPolicyId());
  const claimId = yield select(makeSelectClaimId());
  const stepWhatHappenedData = yield select(makeSelectStepWhatHappened());
  const { dateOfEvent, timeOfEvent } = stepWhatHappenedData;
  if (!byClaimId && (!dateOfEvent || !timeOfEvent)) {
    return;
  }
  const incidentDate = combineDateTime(dateOfEvent, timeOfEvent);
  const requestURL = (byClaimId) ?
    `${apiConfig.restServiceDomain}/api/v1/policies/${policyId}/claimTypes?claimParentId=${claimId}` :
    `${apiConfig.restServiceDomain}/api/v1/policies/${policyId}/claimTypes?incidentDate=${incidentDate}`;

  const requestOptions = {
    headers: {
      Authorization: `bearer ${token}`,
    },
  };

  try {
    yield put(startProgress());
    const res = yield call(request, requestURL, requestOptions);
    const policyTypeId = yield select(makeSelectPolicyTypeId());
    const policyTypeCode = yield select(makeSelectPolicyTypeCode());
    const policyPlanIdsRaw = yield select(makeSelectPolicyPlanIds());
    const policyPlanIds = !!policyPlanIdsRaw && typeof policyPlanIdsRaw.toJS === 'function'
      ? policyPlanIdsRaw.toJS()
      : (policyPlanIdsRaw || []);
    const jsonVersion = yield select(makeSelectJSONVersion());
    const showZurichUHC = yield select(makeSelectShowZurichUHC());
    yield put(setClaimTypes([...res.payload.claimTypeDtos], policyTypeId));
    yield put(setChildBenefits([...res.payload.childBenefitDtos], policyTypeId));
    yield put(
      getClaimTypeSuccess({ claimTypeList: res.payload.claimTypeDtos, policyTypeId, jsonVersion, showZurichUHC, policyTypeCode, policyPlanIds })
    );
    yield put(
      getInitClaimTypeSuccess({ claimTypeList: res.payload.claimTypeDtos })
    );
  } catch (err) {
    if (!isAuthorised(err.response)) {
      yield put(resetLoginInfo());
      yield put(logoutSuccess());
    } else {
      yield put(showErrorToastr(err.message));
    }
  } finally {
    yield put(endProgress());
  }
}

function* validateEventDateTime({ fieldName }) {
  try {
    yield put(startProgress());
    const token = yield select(makeSelectAuthToken());
    const policyId = yield select(makeSelectPolicyId());
    const stepWhatHappenedData = yield select(makeSelectStepWhatHappened());
    const { dateOfEvent, timeOfEvent } = stepWhatHappenedData;
    if (!dateOfEvent || !timeOfEvent) {
      return;
    }
    const incidentDate = combineDateTime(dateOfEvent, timeOfEvent);
    const data = { incidentDate };
    const requestUrl = `${apiConfig.restServiceDomain}/api/v1/policies/${policyId}/claims/validateIncidentDate`;
    const requestOption = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `bearer ${token}`,
      },
      method: 'POST',
      body: JSON.stringify(data),
    };
    const response = yield call(request, requestUrl, requestOption);
    if (response.isSuccess) {
      if (!response.payload.isValid) {
        // yield put(handleFormDataChange(fieldName, null));
        // yield put(handleFormDataChange('form.selectedBenefits', []));
        yield put(setErrorMessageDateValidation(mapDateValidationErrorMessage(response.payload.errorMessage)));
      } else {
        yield put(setErrorMessageDateValidation(''));
        // yield loadClaimTypeList();
        yield validatePolicyVersionChange();
      }
    }
  } catch (err) {
    if (!isAuthorised(err.response)) {
      yield put(resetLoginInfo());
      yield put(logoutSuccess());
    } else {
      yield put(showErrorToastr(err.message));
    }
  } finally {
    yield put(endProgress());
  }
}

function* validatePolicyVersionChange() {
  const token = yield select(makeSelectAuthToken());
  const policyId = yield select(makeSelectPolicyId());
  const stepWhatHappenedData = yield select(makeSelectStepWhatHappened());
  const { dateOfEvent, timeOfEvent } = stepWhatHappenedData;
  if (!dateOfEvent || !timeOfEvent) {
    return;
  }
  const newIncidentDate = combineDateTime(dateOfEvent, timeOfEvent);
  const { oldEventDate, oldEventTime } = yield select(makeSelectOldEventDate());
  const incidentDate = combineDateTime(oldEventDate, oldEventTime);
  const data = {
    incidentDate,
    newIncidentDate,
  };
  const requestUrl = `${apiConfig.restServiceDomain}/api/v1/policies/${policyId}/validatePolicyVersionChange`;
  const requestOption = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `bearer ${token}`,
    },
    body: JSON.stringify(data),
  };

  try {
    yield put(startProgress());
    const response = yield call(request, requestUrl, requestOption);
    if (response.isSuccess) {
      if (response.payload.isPolicyVersionChange) {
        // show warning
        yield put(policyVersionChanged(true));
      } else {
        yield put(handleFormDataChange('oldEventDate', dateOfEvent));
        yield put(handleFormDataChange('oldEventTime', timeOfEvent));
        yield loadClaimTypeList();
      }
    }
  } catch (err) {
    if (!isAuthorised(err.response)) {
      yield put(resetLoginInfo());
      yield put(logoutSuccess());
    } else {
      yield put(showErrorToastr(err.message));
    }
  } finally {
    yield put(endProgress());
  }
}

export function* triggerWhatHappenedValidation() {
  try {
    const token = yield select(makeSelectAuthToken());
    const claimId = yield select(makeSelectClaimId());
    const stepWhatHappenedData = yield select(makeSelectStepWhatHappened());
    const { dateOfEvent, timeOfEvent, storyEvent, city, selectedCountry } = stepWhatHappenedData;

    // Format EventDate: YYYY-MM-DD or null
    let eventDate = null;
    if (dateOfEvent) {
      const momentDate = moment(dateOfEvent);
      if (momentDate.isValid()) {
        eventDate = momentDate.format('YYYY-MM-DD');
      }
    }

    // Format EventTime: HH:mm or null
    let eventTime = null;
    if (timeOfEvent) {
      const momentTime = moment(timeOfEvent);
      if (momentTime.isValid()) {
        eventTime = momentTime.format('HH:mm');
      }
    }

    // Format EventDescription: trimmed storyEvent or null
    const eventDescription = storyEvent && storyEvent.trim() ? storyEvent.trim() : null;

    // Format EventLocation: "{cityLabel}, {countryLabel}" or null
    let eventLocation = null;
    const cityObj = city && typeof city.toJS === 'function' ? city.toJS() : city;
    const countryObj = selectedCountry && typeof selectedCountry.toJS === 'function' ? selectedCountry.toJS() : selectedCountry;
    
    if (cityObj && countryObj) {
      const cityLabel = cityObj.label || cityObj.value || cityObj.name || '';
      const countryLabel = countryObj.label || countryObj.value || countryObj.name || '';
      if (cityLabel && countryLabel) {
        eventLocation = `${cityLabel}, ${countryLabel}`;
      }
    }

    const requestURL = `${apiConfig.restServiceDomain}/rest/documentValidation/claims/parent/${claimId}/triggerWhatHappenedValidation`;
    const requestOptions = {
      method: 'POST',
      headers: {
        Authorization: `bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        EventDate: eventDate,
        EventTime: eventTime,
        EventDescription: eventDescription,
        EventLocation: eventLocation,
      }),
    };

    const response = yield call(request, requestURL, requestOptions);
    
    if (response.Success) {
      // Normalize IsSufficient: can be boolean true/false or string "true"/"false"
      let isSufficient = response.IsSufficient;
      if (typeof isSufficient === 'string') {
        isSufficient = isSufficient.toLowerCase() === 'true';
      }
      const feedbackMessage = response.FeedbackMessage || null;
      
      yield put(triggerWhatHappenedValidationSuccess(isSufficient, feedbackMessage));
    } else {
      yield put(triggerWhatHappenedValidationFailure());
    }
  } catch (err) {
    if (!isAuthorised(err.response)) {
      yield put(resetLoginInfo());
      yield put(logoutSuccess());
    } else {
      // Silently fail - don't show toastr to avoid interrupting user flow
      yield put(triggerWhatHappenedValidationFailure());
    }
  }
}

/**
 * Root saga manages watcher lifecycle
 */
export default function* stepWhatHappened() {
  yield [
    takeLatest(GET_COUNTRY_LIST, getCountries),
    takeLatest(GET_COUNTRY_SUPPORTED_LIST, getCountriesOfxSupported),
    takeLatest(GET_CITY_LIST, getCities),
    takeLatest(GET_PROVINCE_LIST, getProvinces),
    takeLatest(GET_CLAIM_TYPES, loadClaimTypeList),
    takeLatest(VALIDATE_DATE_TIME_OF_EVENT, validateEventDateTime),
    takeLatest(TRIGGER_WHAT_HAPPENED_VALIDATION, triggerWhatHappenedValidation),
  ];
}



// WEBPACK FOOTER //
// ./app/containers/FastCover/StepWhatHappened/saga.js